// Canvas.js
import { Spin, Upload, UploadFile, UploadProps } from 'antd';
import { Canvas, FabricImage, Point, Rect, TMat2D } from 'fabric'; // browser
import jsPDF from 'jspdf';
import { useEffect, useRef, useState } from 'react';
import orderApi from '../../apis/order';
import { INITIAL_ZOOM, PHONE_CASE_MODELS } from '../../constants/phoneCase';
import { IDesign } from '../../interfaces/Order';
import { PhoneCaseModel } from '../../interfaces/Phone';
import { FlexContainer } from '../../styles/Global';
import utils from '../../utils/utils';
import Button from '../Button/Button';
import { SpinContainer, StyledSelect } from '../Order/Order.styled';

interface PhoneCaseEditorProps {
  onChangePhoneModel: (phoneModel: PhoneCaseModel) => void;
  orderId: string;
  onDesignSubmitted: (design: IDesign) => void;
}

const PhoneCaseEditor = (props: PhoneCaseEditorProps) => {
  const { onChangePhoneModel, orderId, onDesignSubmitted } = props;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fabricCanvas = useRef<Canvas | null>(null);
  const isDragging = useRef(false);
  const fabricImage = useRef<FabricImage | null>(null);
  const overlayImage = useRef<FabricImage | null>(null);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [enabledPanning, setEnabledPanning] = useState<boolean>(false);
  const [phoneModel, setPhoneModel] = useState<string>(
    PHONE_CASE_MODELS[0].model
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    FabricImage.fromURL(phoneModel, {}, { opacity: 0.85 }).then((image) => {
      if (!canvasRef.current) {
        return;
      }

      overlayImage.current = image;

      fabricCanvas.current = new Canvas(canvasRef.current, {
        preserveObjectStacking: true,
        overlayImage: image,
      });

      // Set initial canvas size
      resizeCanvas();

      setZoom(INITIAL_ZOOM);

      // Add event listener for window resize
      window.addEventListener('resize', () => resizeCanvas());

      fabricCanvas.current.renderAll();
    });

    // Clean up canvas instance when component unmounts
    return () => {
      fabricCanvas.current?.dispose();
      window.removeEventListener('resize', () => resizeCanvas());
    };
  }, []);

  useEffect(() => {
    if (!!fabricCanvas.current) {
      const canvas = fabricCanvas.current;
      FabricImage.fromURL(phoneModel, {}, { opacity: 0.85 }).then((image) => {
        overlayImage.current = image;

        canvas.overlayImage = image;

        // Set initial canvas size
        resizeCanvas();

        setZoom(INITIAL_ZOOM);

        resizeImageFitOverlay();

        canvas.renderAll();
      });
    }

    const model = getModel();

    if (!!model) {
      onChangePhoneModel(model.value);
    }
  }, [phoneModel]);

  useEffect(() => {
    if (!!fileList?.length) {
      FabricImage.fromURL(
        URL.createObjectURL(fileList[0]!.originFileObj as Blob)
      ).then((img) => {
        const overlay = overlayImage.current;

        // Set initial position to the center of the canvas
        img.set({
          originX: 'center',
          originY: 'center',
          scaleX: (overlay?.width ?? img.width) / img.width,
          scaleY: (overlay?.height ?? img.height) / img.height,
          selectable: true,
        });

        fabricImage.current = img;

        // Add the image to the canvas
        fabricCanvas.current?.add(img);
        fabricCanvas.current?.centerObject(img);
        fabricCanvas.current?.setActiveObject(img);
      });
    } else if (!!fabricImage.current) {
      fabricCanvas.current?.remove(fabricImage.current);
      fabricImage.current = null;
    }
  }, [fileList?.length]);

  useEffect(() => {
    enablePanning();
  }, [enabledPanning]);

  // Function to resize the canvas
  const resizeCanvas = () => {
    if (!fabricCanvas.current) return;

    const width = window.innerWidth - 180;
    const height = window.innerHeight - 80;

    // Update Fabric canvas dimensions
    fabricCanvas.current?.setDimensions({ width, height });

    // Scale the image to half the size of the screen
    const image = fabricCanvas.current.overlayImage;
    if (image) {
      image.setXY(
        new Point(
          fabricCanvas.current?.width / 2,
          fabricCanvas.current?.height / 2
        ),
        'center',
        'center'
      );

      fabricCanvas.current.renderAll();
    }
  };

  const getModel = () => {
    if (!!phoneModel) {
      return PHONE_CASE_MODELS.find((m) => m.model === phoneModel);
    }
  };

  const resizeImageFitOverlay = () => {
    if (!fabricImage.current || !overlayImage.current) return;
    const img = fabricImage.current;
    const overlay = overlayImage.current;

    img.set({
      originX: 'center',
      originY: 'center',
      scaleX: (overlay?.width ?? img.width) / img.width,
      scaleY: (overlay?.height ?? img.height) / img.height,
      selectable: true,
    });
  };

  // Enable Panning
  const enablePanning = () => {
    if (!fabricCanvas.current || !enabledPanning) return;

    // Attach mouse event listeners for panning
    fabricCanvas.current.on('mouse:down', (event) => {
      if (!fabricCanvas.current || !enabledPanning) return;

      isDragging.current = true;
      fabricCanvas.current!.setCursor('grab');
      fabricCanvas.current!.renderAll();
    });

    fabricCanvas.current.on('mouse:move', (event) => {
      if (!fabricCanvas.current || !enabledPanning || !isDragging.current)
        return;

      // Use event.e.movementX and movementY for smoother panning
      const nativeEvent = event.e as MouseEvent;
      const { movementX, movementY } = nativeEvent;
      const viewportTransform = fabricCanvas.current!.viewportTransform;

      // Update viewport transform for panning
      viewportTransform[4] += movementX; // Horizontal panning
      viewportTransform[5] += movementY; // Vertical panning

      fabricCanvas.current?.setViewportTransform(viewportTransform);
    });

    fabricCanvas.current.on('mouse:up', () => {
      if (!fabricCanvas.current || !enabledPanning) return;

      isDragging.current = false;
      fabricCanvas.current!.setCursor('default');
      fabricCanvas.current!.renderAll();
    });
  };

  const setClippingToOverlay = () => {
    const canvas = fabricCanvas.current;

    if (!canvas || !canvas.overlayImage) {
      console.warn('Canvas or overlay image not available.');
      return;
    }

    // const overlayImage = canvas.overlayImage;

    if (!overlayImage.current) {
      return;
    }

    // Get the dimensions and position of the overlay image
    const { left, top, width, height, scaleX, scaleY } = overlayImage.current;

    // Calculate the scaled dimensions
    const scaledWidth = width * scaleX;
    const scaledHeight = height * scaleY;

    // Calculate the top-left corner
    const clipLeft = left;
    const clipTop = top;

    // Define a rectangle for clipping
    const clipRect = new Rect({
      left: clipLeft,
      top: clipTop,
      width: scaledWidth,
      height: scaledHeight,
      absolutePositioned: true, // Ensure it's positioned in absolute canvas coordinates
    });

    canvas.clipPath = clipRect;

    // Re-render to apply the clipPath
    canvas.requestRenderAll();
  };

  const cloneCanvas = (sourceCanvas: Canvas): Canvas => {
    // Create a new canvas element
    const clonedCanvas = new Canvas(undefined);

    // Serialize the source canvas to JSON
    const json = sourceCanvas.toJSON();

    // Load the JSON into the new canvas
    clonedCanvas.loadFromJSON(json, () => {
      // Ensure the new canvas is rendered
      clonedCanvas.renderAll();
    });

    // Set dimensions to match the source canvas
    clonedCanvas.setDimensions({
      width: sourceCanvas.width,
      height: sourceCanvas.height,
    });

    return clonedCanvas;
  };

  const exportClippedCanvas = () => {
    const canvas = fabricCanvas.current;
    const overlay = overlayImage.current;

    if (!canvas || !overlay) {
      console.warn('Canvas or overlay image not available.');
      return;
    }

    // Save the current canvas state
    const originalWidth = canvas.width;
    const originalHeight = canvas.height;
    const originalViewportTransform = [...(canvas.viewportTransform || [])];
    const { width, height } = overlay;

    // Set the canvas to the overlay image's size
    canvas.setDimensions({
      width: width,
      height: height,
    });

    canvas.overlayImage = undefined;

    // Align viewport to the overlay
    canvas.viewportTransform = [
      1, // Scale X
      0, // Skew X
      0, // Skew Y
      1, // Scale Y
      -overlay.left, // Translate X
      -overlay.top, // Translate Y
    ];

    setZoom(1);

    // Export canvas as an image
    const imageDataUrl = canvas.toDataURL({
      format: 'png',
      left: overlay.left,
      top: overlay.top,
      width: overlay.width,
      height: overlay.height,
      multiplier: 1,
    });

    // Restore original canvas state
    canvas.setDimensions({
      width: originalWidth,
      height: originalHeight,
    });

    canvas.viewportTransform = originalViewportTransform as TMat2D;

    canvas.overlayImage = overlay;

    canvas.requestRenderAll();

    const doc = new jsPDF({
      orientation: 'portrait', // or simply 'p'
      format: [148, 238],
    });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Add orderId to the top-left corner
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(`Order ID: ${orderId}`, 10, 10); // Position: (x, y)

    const model = getModel();

    // Add phoneModel to the top-right corner
    doc.text(
      model?.label ?? '',
      pageWidth / 2,
      10,
      { align: 'center' } // Right-aligned
    );

    // Center the image
    const marginX = (pageWidth - utils.pxToMm(width)) / 2;
    const marginY = (pageHeight - utils.pxToMm(height)) / 2;

    // Add image to the PDF
    doc.addImage(
      imageDataUrl,
      'png',
      marginX,
      marginY,
      utils.pxToMm(width),
      utils.pxToMm(height)
    );

    // Generate the PDF as a Blob
    const pdfBlob = doc.output('blob');

    return { exportedImage: utils.dataUrlToBlob(imageDataUrl), pdf: pdfBlob };
  };

  const setZoom = (zoom: number, canvasToZoom?: Canvas) => {
    const canvas = canvasToZoom ?? fabricCanvas.current;
    if (!canvas) {
      console.warn('Canvas not initialized.');
      return;
    }

    canvas.setZoom(zoom);

    // Optionally adjust the viewport transform to center the content
    const viewportTransform = canvas.viewportTransform;
    if (viewportTransform) {
      viewportTransform[4] = (canvas.width / 2) * (1 - zoom); // Center horizontally
      viewportTransform[5] = (canvas.height / 2) * (1 - zoom); // Center vertically
      canvas.setViewportTransform(viewportTransform);
    }

    canvas.requestRenderAll(); // Re-render to apply the changes
  };

  const previewPdf = () => {
    const { pdf } = exportClippedCanvas() ?? {};

    if (!!pdf) {
      // Create a preview URL
      const previewUrl = URL.createObjectURL(pdf);

      // Open the preview in a new window or tab
      window.open(previewUrl, '_blank');
    }
  };

  const submitDesign = async () => {
    try {
      setLoading(true);
      const { exportedImage, pdf } = exportClippedCanvas() ?? {};
      if (!exportedImage || !pdf) {
        return;
      }

      const formData = new FormData();

      formData.append('design', pdf);
      formData.append('originalFile', exportedImage);
      const model = getModel();
      formData.append('caseModel', model?.value ?? '');

      const res = await orderApi.createPhoneCaseDesign(formData);
      onDesignSubmitted(res);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenFile: UploadProps['onChange'] = (file: any) => {
    setFileList(
      file?.fileList.length
        ? [
            {
              ...file.fileList[0],
              status:
                file.fileList[0].status === 'error'
                  ? 'done'
                  : file.fileList[0].status,
            },
          ]
        : file?.fileList
    );
  };

  return (
    <div>
      {loading && (
        <SpinContainer>
          <Spin size="large" />
        </SpinContainer>
      )}
      <FlexContainer style={{ padding: 10 }}>
        <StyledSelect
          className="w-100 m__r--10"
          placeholder="Choose Model"
          // value={types}
          onChange={(val: any) => {
            setPhoneModel(val);
          }}
          options={PHONE_CASE_MODELS?.map((phone) => ({
            label: phone.label,
            value: phone.model,
          }))}
          value={phoneModel}
        />
        <Upload
          name="file"
          onChange={handleOpenFile}
          multiple={false}
          fileList={fileList}
          accept="image/*"
        >
          {fileList?.length < 1 && (
            <Button className="font-weight--500 m__r--10 w-100" key="3">
              Open Design File
            </Button>
          )}
        </Upload>
        {fileList.length > 0 && (
          <Button
            className="font-weight--500 m__l--10 w-20"
            onClick={() => setFileList([])}
          >
            Remove Design{' '}
          </Button>
        )}

        {/* <button
          type="button"
          onClick={() => setEnabledPanning((panning) => !panning)}
        >
          {!enabledPanning ? 'Panning' : 'Disable Panning'}
        </button> */}
        {!!fileList.length && (
          <>
            <Button
              onClick={previewPdf}
              className="font-weight--500 m__l--20 w-20"
              key="4"
            >
              Preview
            </Button>
            <Button
              onClick={submitDesign}
              className="font-weight--500 m__l--20 w-20"
              key="5"
            >
              Submit Design
            </Button>
          </>
        )}
      </FlexContainer>

      <div
        id="canvas-container"
        style={{
          width: '100%', // Adjust based on available space
          height: 'calc(100vh - 120px)', // Adjust based on available space
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          border: '2px solid #007BFF', // Add a border to indicate the canvas area
          borderRadius: '8px', // Optional: add rounded corners
          boxSizing: 'border-box', // Ensure border doesn't affect dimensions
          overflow: 'hidden', // Prevent content from spilling out
        }}
      >
        <canvas ref={canvasRef} />
      </div>
    </div>
  );
};

export default PhoneCaseEditor;
